/**
 * Profile resource.
 *
 * GET /brands/me → BrandProfile (the brand entity for the authenticated brand_owner).
 * PUT/DELETE /brands/me and legacy /brand/profile/* paths are NOT registered on the BE.
 * Profile editing is deferred to v3.
 */
import { apiClient } from '@discovr/common';
import type { DataResponse } from '@discovr/common';

/**
 * Brand entity returned by GET /brands/me.
 * Matches internal/brands/domain/brand.go Brand struct.
 */
export interface BrandProfile {
  id: string;
  owner_id: string;
  slug?: string;
  name: string;
  website?: string;
  socials?: {
    instagram?: string;
    youtube?: string;
  };
  brand_logo?: string;
  created_at: string;
  updated_at: string;
}

/**
 * User profile shape returned by GET /profile (shared endpoint).
 * Matches internal/users/domain/user.go User struct.
 * `scopes` is populated for admin_member users (scope-gated access).
 */
export interface UserProfile {
  id: string;
  uid: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  scopes?: string[];
  status: string;
  email_verified: boolean;
  avatar?: string;
}

export interface UpdateProfileBody {
  first_name?: string;
  last_name?: string;
}

export const profile = {
  /** GET /brands/me — returns brand entity. WriteData(w, 200, brand) */
  get: (): Promise<BrandProfile> =>
    apiClient
      .get<DataResponse<BrandProfile>>('/brands/me')
      .then((r) => r.data.data),

  /** PUT /profile — update first_name / last_name for the authenticated user. */
  updateUser: (body: UpdateProfileBody): Promise<UserProfile> =>
    apiClient
      .put<DataResponse<{ user: UserProfile }>>('/profile', body)
      .then((r) => r.data.data.user),

  /** PUT /profile/display-currency — one of the 8 codes, or "" to clear (back to Automatic). */
  setDisplayCurrency: (displayCurrency: string): Promise<string> =>
    apiClient
      .put<DataResponse<{ display_currency: string }>>('/profile/display-currency', { display_currency: displayCurrency })
      .then((r) => r.data.data.display_currency),
};
